import { Num, T, useGT } from 'gt-react';
import { CircleDashedIcon, RobotIcon, UserIcon } from '@phosphor-icons/react';
import type { TaskPageActorCount, TaskPageCount } from '@erp/rpc';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { taskPageBadgeState } from '@/routing/task-pages-nav';

function ActorIcon({
  actorType,
}: {
  actorType: TaskPageActorCount['actorType'];
}) {
  const className = 'size-3.5 shrink-0 text-muted-foreground';
  switch (actorType) {
    case 'user':
      return <UserIcon className={className} />;
    case 'agent':
      return <RobotIcon className={className} />;
    case 'unassigned':
      return <CircleDashedIcon className={className} />;
  }
}

function ActorRow({
  actor,
  isMe,
}: {
  actor: TaskPageActorCount;
  isMe: boolean;
}) {
  const gt = useGT();
  const name =
    actor.actorType === 'unassigned'
      ? gt('Unassigned', { context: 'tasks with no assignee, in a breakdown' })
      : actor.name;
  return (
    <li className="flex items-center gap-2 text-xs">
      <ActorIcon actorType={actor.actorType} />
      <span className={cn('min-w-0 flex-1 truncate', isMe && 'font-medium')}>
        {name}
        {isMe && (
          <span className="text-muted-foreground">
            {' '}
            <T context="suffix after the current user's own name in a per-person breakdown">
              (you)
            </T>
          </span>
        )}
      </span>
      <span className="tabular-nums text-muted-foreground">
        <Num>{actor.count}</Num>
      </span>
    </li>
  );
}

/**
 * Sidebar badge for a task page: the caller's open-task count as a pill, or a
 * quiet dot when the page has open tasks that belong to others. Hovering
 * either shows the org-wide breakdown by assignee (users, agents, unassigned)
 * in descending order.
 */
export function TaskPageCountBadge({
  count,
  currentUserId,
}: {
  count: TaskPageCount;
  currentUserId: string | undefined;
}) {
  const gt = useGT();
  const state = taskPageBadgeState(count);
  if (!state) return null;

  const label =
    state.kind === 'mine'
      ? gt('{count} open tasks assigned to you', {
          context: 'sidebar badge on a task page',
          count: String(count.mine),
        })
      : gt('{count} open tasks, none assigned to you', {
          context: 'sidebar marker on a task page',
          count: String(count.total),
        });

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        {/* A button so keyboard users can focus it: the Base UI trigger opens
            on focus as well as hover. The dot keeps a 20px hit target. */}
        <button
          type="button"
          aria-label={label}
          className={cn(
            'ring-sidebar-ring flex h-5 min-w-5 cursor-default items-center justify-center rounded-full outline-hidden focus-visible:ring-2',
            state.kind === 'mine' &&
              'bg-sidebar-accent-foreground/8 text-sidebar-foreground/70 px-1.5 text-[11px] leading-5',
          )}
        >
          {state.kind === 'mine' ? (
            state.label
          ) : (
            <span
              aria-hidden="true"
              className="bg-sidebar-foreground/30 block size-1.5 rounded-full"
            />
          )}
        </button>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-56 p-2">
        <div className="mb-1.5 flex items-center justify-between text-xs font-medium">
          <T context="heading of a per-assignee breakdown of a task page's open tasks">
            Open tasks
          </T>
          <span className="tabular-nums">
            <Num>{count.total}</Num>
          </span>
        </div>
        <ul className="flex flex-col gap-1">
          {count.byActor.map((actor) => (
            <ActorRow
              key={`${actor.actorType}:${actor.actorId ?? ''}`}
              actor={actor}
              isMe={
                actor.actorType === 'user' &&
                currentUserId !== undefined &&
                actor.actorId === currentUserId
              }
            />
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  );
}
